import { makeAutoObservable, runInAction } from "mobx";
import AlertSocketHandler from "../handlers/ws/AlertSocketHandler";
import alertApiHandler from "../handlers/AlertApiHandler";
import userStore from "./UserStore";

class AlertNotificationStore {
  // alerts = [];
  alertsMap = new Map();
  alertList = []; // outer tab
  readAlertIds = new Set();

  readStatusByAlert = new Map(); // alertId -> list of userIDs

  alertStatusReadByUser = new Map(); // alertId -> list of userNames

  isLoading = false;
  isReadStatusLoaded = false;
  error = null;

  earliestAlertId = null;
  reachedStart = false;

  constructor() {
    makeAutoObservable(this);
    AlertSocketHandler.subscribe(this.handleWebSocketMessage.bind(this));
  }

  handleWebSocketMessage(data) {
    if (data.type === "alert_notification") {
      runInAction(() => {
        const alertId = data._id;
        if (!this.alertsMap.has(alertId)) {
          this.alertsMap.set(alertId, data);
          this.alertList.unshift(alertId);
        }
      });
    }
  }

  async fetchAllAlerts(
    device_ids = [],
    start,
    end,
    limit = 100,
    before_id = null
  ) {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await alertApiHandler.post("/alerts/notifications", {
        device_ids,
        start,
        end,
        limit,
        before_id,
      });

      console.log("Here is all alert notifications: ", response);
      const newAlerts = response?.alerts || [];

      runInAction(() => {
        newAlerts.forEach((alert) => {
          const rawId = alert._id?.$oid || alert._id;

          if (!rawId || typeof rawId !== "string") {
            console.warn("❌ Skipping alert due to invalid _id:", alert._id);
            return;
          }

          // console.log("✅ Alert ID being inserted:", rawId);
          // console.log("🧠 Is in map?", this.alertsMap.has(rawId));

          this.alertsMap.set(rawId, alert);

          if (!this.alertList.includes(rawId)) {
            this.alertList.unshift(rawId);
          }
        });

        this.earliestAlertId =
          newAlerts.length > 0
            ? newAlerts[newAlerts.length - 1]._id?.$oid ||
              newAlerts[newAlerts.length - 1]._id
            : this.earliestAlertId;

        this.reachedStart = response?.reached_start ?? false;
      });
    } catch (error) {
      console.error("❌ fetchAlerts error:", error);
      runInAction(() => {
        this.error = error?.message || "Failed to fetch alerts";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  get alerts() {
    return this.alertList.map((id) => this.alertsMap.get(id)).filter(Boolean);
  }

  getAlertById(alertId) {
    console.log("Here is the alertID: ", alertId);
    return this.alertsMap.get(alertId);
  }

  get isReachedStart() {
    return this.reachedStart;
  }

  get getEarliestId() {
    return this.earliestAlertId;
  }

  async fetchUserReadAlertIds(userId) {
    this.isReadStatusLoaded = false;
    try {
      const response = await alertApiHandler.get(
        `/alerts/user-read-alerts/${userId}`
      );
      console.log("Here is all the read alert ids: ", response);
      runInAction(() => {
        this.readAlertIds = new Set(response?.read_alert_ids || []);
        this.isReadStatusLoaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.isReadStatusLoaded = true;
      });
      console.error("❌ fetchUserReadAlertIds error:", err);
    }
  }

  isAlertReadByUser(alertId) {
    return this.readAlertIds.has(alertId);
  }

  async markAlertAsRead(alertId, userId) {
    console.log("Here is userId: ", userId);
    try {
      const response = await alertApiHandler.post(
        `/alerts/${alertId}/mark-read`,
        {
          user_id: userId,
        }
      );

      console.log("Here is the response: ", response);

      runInAction(() => {
        this.readAlertIds.add(alertId);

        const existingUsers = this.readStatusByAlert.get(alertId) || [];
        if (!existingUsers.includes(userId)) {
          this.readStatusByAlert.set(alertId, [...existingUsers, userId]);
        }
      });
    } catch (err) {
      console.error(`❌ markAlertAsRead error (alert ${alertId}):`, err);
    }
  }

  async markAlertAsUnread(alertId, userId) {
    try {
      await alertApiHandler.post(`/alerts/${alertId}/mark-unread`, {
        user_id: userId,
      });

      runInAction(() => {
        this.readAlertIds.delete(alertId);

        const existingUsers = this.readStatusByAlert.get(alertId) || [];
        this.readStatusByAlert.set(
          alertId,
          existingUsers.filter((uid) => uid !== userId)
        );
      });
    } catch (err) {
      console.error(`❌ markAlertAsUnread error (alert ${alertId}):`, err);
    }
  }

  async fetchReadStatusForSpecificAlertId(alertId) {
    console.log("Here is all user: ", userStore.allUsers);
    try {
      const response = await alertApiHandler.get(
        `/alerts/read-status/${alertId}`
      );
      console.log("Read status: ", response);

      const { read_by = [] } = response;

      const userNames = read_by.map((userId) => {
        const match = userStore.allUsers.find((u) => u.id === userId);
        return match?.name || `User ID: ${userId}`;
      });

      console.log("Users that read this alert: ", userNames);

      const users = response?.read_by || [];

      runInAction(() => {
        this.readStatusByAlert.set(alertId, users);
        this.alertStatusReadByUser.set(alertId, userNames);
      });
    } catch (err) {
      console.error(`❌ fetchReadStatusForAlert error (${alertId}):`, err);
    }
  }

  async fetchReadStatusForMultipleAlertIds(alertIds) {
    if (!Array.isArray(alertIds)) {
      console.log("Expected array in fetchReadStatusForAlertIds");
      return;
    }

    const missingIds = alertIds.filter((id) => !this.readStatusByAlert.has(id));
    if (missingIds.length === 0) {
      return alertIds.map((id) => this.readStatusByAlert.get(id));
    }

    try {
      const data = await alertApiHandler.post("/alerts/read-status-by-alerts", {
        alert_ids: alertIds,
      });

      runInAction(() => {
        // this.readStatusByAlert.clear();
        const status = data?.read_status || {};
        Object.entries(status).forEach(([alertId, userList]) => {
          this.readStatusByAlert.set(alertId, userList || []);
        });
      });
    } catch (err) {
      console.error("❌ fetchReadStatusForAlertIds error:", err);
    }
  }

  // get isAlertReadByUser() {
  //   return (alertId) => this.readAlertIds.includes(alertId);
  // }

  getAlertReadByUserIds(alertId) {
    return this.readStatusByAlert.get(alertId) || [];
  }

  getAlertReadByUserNames(alertId) {
    return this.alertStatusReadByUser.get(alertId) || [];
  }

  reset() {
    this.alertsMap.clear();
    this.alertList = [];
    this.readAlertIds.clear();
    this.readStatusByAlert.clear();
    this.reachedStart = false;
    this.earliestAlertId = null;
  }
}

const alertNotificationStore = new AlertNotificationStore();
export default alertNotificationStore;
